import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp } from "@fortawesome/free-solid-svg-icons";
import "./pickupAssignmentWorkloadTags.scss";

export function tagCountLabel(completed: number, total: number): string {
  if (total <= 0) return "—";
  return `${completed}/${total}`;
}

export function completionPercent(completed: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((Math.min(Math.max(completed, 0), total) / total) * 100);
}

/** Progress fill by % complete: red (0–25%), orange (26–50%), blue (51–75%), green (76–100%). */
export function completionTier(pct: number): "red" | "orange" | "blue" | "green" {
  if (pct <= 25) return "red";
  if (pct <= 50) return "orange";
  if (pct <= 75) return "blue";
  return "green";
}

export const DeliveryWorkTag: React.FC<{
  completed: number;
  total: number;
  className?: string;
  /** Same pill as compact, with “Deliveries” before the count. */
  showTypeLabel?: boolean;
}> = ({ completed, total, className, showTypeLabel }) => (
  <span
    className={`pa-work-tag pa-work-tag--delivery${showTypeLabel ? " pa-work-tag--labeled" : ""}${
      className ? ` ${className}` : ""
    }`}
  >
    <FontAwesomeIcon icon={faArrowDown} className="pa-work-tag__icon" aria-hidden />
    {showTypeLabel ? (
      <>
        <span className="pa-work-tag__type-label">Deliveries</span>
        <span className="pa-work-tag__sep" aria-hidden>
          {" "}
          ·{" "}
        </span>
      </>
    ) : null}
    {tagCountLabel(completed, total)}
  </span>
);

export const PickupWorkTag: React.FC<{
  completed: number;
  total: number;
  className?: string;
  /** Same pill as compact, with “Pickups” before the count. */
  showTypeLabel?: boolean;
}> = ({ completed, total, className, showTypeLabel }) => (
  <span
    className={`pa-work-tag pa-work-tag--pickup${showTypeLabel ? " pa-work-tag--labeled" : ""}${
      className ? ` ${className}` : ""
    }`}
  >
    <FontAwesomeIcon icon={faArrowUp} className="pa-work-tag__icon" aria-hidden />
    {showTypeLabel ? (
      <>
        <span className="pa-work-tag__type-label">Pickups</span>
        <span className="pa-work-tag__sep" aria-hidden>
          {" "}
          ·{" "}
        </span>
      </>
    ) : null}
    {tagCountLabel(completed, total)}
  </span>
);

export type WorkTypeProgressBarProps = {
  variant: "delivery" | "pickup";
  completed: number;
  total: number;
  /** `completion` = fill color from % done; `workType` = legacy blue/purple fills. */
  colorMode?: "completion" | "workType";
  className?: string;
};

export const WorkTypeProgressBar: React.FC<WorkTypeProgressBarProps> = ({
  variant,
  completed,
  total,
  colorMode = "completion",
  className,
}) => {
  if (total <= 0) return null;
  const valueNow = Math.min(Math.max(completed, 0), total);
  const pct = completionPercent(valueNow, total);
  const tier = completionTier(pct);
  const noun = variant === "delivery" ? "Deliveries" : "Pickups";
  const fillClass =
    colorMode === "completion"
      ? `pa-work-progress__fill pa-work-progress__fill--tier-${tier}`
      : `pa-work-progress__fill pa-work-progress__fill--type-${variant}`;

  return (
    <div
      className={`pa-work-progress pa-work-progress--${variant}${className ? ` ${className}` : ""}`}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={valueNow}
      aria-label={`${noun}: ${valueNow} of ${total} complete (${pct}%)`}
    >
      <div className="pa-work-progress__track">
        <div className={fillClass} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

/**
 * Expanded workload: same pill as compact plus “Deliveries” / “Pickups”, under-tag bar.
 * `% complete` is opt-in (`showPercentLine`) so it appears on hover flyouts only by default.
 */
export const WorkTypeExpandedCard: React.FC<{
  variant: "delivery" | "pickup";
  completed: number;
  total: number;
  className?: string;
  showPercentLine?: boolean;
}> = ({ variant, completed, total, className, showPercentLine = false }) => {
  const pct = completionPercent(completed, total);
  const pctLabel = total <= 0 ? "—" : `${pct}% complete`;

  return (
    <span
      className={`pa-work-tag-stack pa-work-tag-stack--expanded pa-work-tag-stack--${variant}${
        className ? ` ${className}` : ""
      }`}
    >
      {variant === "delivery" ? (
        <DeliveryWorkTag completed={completed} total={total} showTypeLabel />
      ) : (
        <PickupWorkTag completed={completed} total={total} showTypeLabel />
      )}
      {total > 0 ? (
        <WorkTypeProgressBar variant={variant} completed={completed} total={total} colorMode="completion" />
      ) : null}
      {showPercentLine ? <span className="pa-work-tag-stack__pct">{pctLabel}</span> : null}
    </span>
  );
};

/** Tag + progress bar; bar width matches tag (shrink-to-tag column). */
export const WorkTypeTagStack: React.FC<{
  variant: "delivery" | "pickup";
  completed: number;
  total: number;
}> = ({ variant, completed, total }) => {
  const Tag = variant === "delivery" ? DeliveryWorkTag : PickupWorkTag;
  return (
    <span className="pa-work-tag-stack">
      <Tag completed={completed} total={total} />
      {total > 0 ? (
        <WorkTypeProgressBar variant={variant} completed={completed} total={total} colorMode="completion" />
      ) : null}
    </span>
  );
};

const HOVER_CLOSE_MS = 160;

/** Tag stack + hover flyout (portal + fixed) with expanded card and % — avoids list `overflow` clipping. */
export const WorkTypeTagStackWithHover: React.FC<{
  variant: "delivery" | "pickup";
  completed: number;
  total: number;
}> = ({ variant, completed, total }) => {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const closeTimerRef = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const updateCoords = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setCoords({ top: r.bottom + 6, left: r.left + r.width / 2 });
  }, []);

  const openFlyout = useCallback(() => {
    clearCloseTimer();
    updateCoords();
    setOpen(true);
  }, [clearCloseTimer, updateCoords]);

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      setOpen(false);
      setCoords(null);
    }, HOVER_CLOSE_MS);
  }, [clearCloseTimer]);

  useEffect(() => {
    if (!open) return undefined;
    const onScrollOrResize = () => updateCoords();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, updateCoords]);

  const flyout =
    open &&
    coords &&
    createPortal(
      <div
        className="pa-work-hover-flyout"
        style={{ top: coords.top, left: coords.left }}
        role="tooltip"
        onMouseEnter={clearCloseTimer}
        onMouseLeave={scheduleClose}
      >
        <WorkTypeExpandedCard
          variant={variant}
          completed={completed}
          total={total}
          showPercentLine
        />
      </div>,
      document.body
    );

  return (
    <>
      <span
        ref={anchorRef}
        className="pa-work-hover-anchor"
        onMouseEnter={openFlyout}
        onMouseLeave={scheduleClose}
      >
        <WorkTypeTagStack variant={variant} completed={completed} total={total} />
      </span>
      {flyout}
    </>
  );
};

export type PickupAssignmentWorkloadBlockProps = {
  deliveriesCompleted: number;
  numberOfDeliveries: number;
  pickupsCompleted: number;
  numberOfPickups: number;
  showDelivery?: boolean;
  showPickup?: boolean;
  /**
   * `tags` — compact tags with bar only under each tag; hover shows expanded card + %.
   * `expandedCardsOnly` — two expanded cards only (e.g. “Workload in view”).
   */
  layout?: "tags" | "expandedCardsOnly";
  /** When `layout` is `tags`, enables hover popover (off for dense nested rows if needed). */
  showHoverExpanded?: boolean;
  compact?: boolean;
  className?: string;
};

/**
 * Shared delivery + pickup workload: either inline tag stacks with under-tag bars + hover,
 * or two expanded summary cards only.
 */
export const PickupAssignmentWorkloadBlock: React.FC<PickupAssignmentWorkloadBlockProps> = ({
  deliveriesCompleted,
  numberOfDeliveries,
  pickupsCompleted,
  numberOfPickups,
  showDelivery = true,
  showPickup = true,
  layout = "tags",
  showHoverExpanded = true,
  compact = false,
  className,
}) => {
  const rootClass = [
    "pa-work-block",
    layout === "expandedCardsOnly" ? "pa-work-block--expanded-only" : "",
    compact ? "pa-work-block--compact" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  if (layout === "expandedCardsOnly") {
    return (
      <div className={rootClass}>
        <div className="pa-work-block__expanded-row">
          {showDelivery ? (
            <WorkTypeExpandedCard
              variant="delivery"
              completed={deliveriesCompleted}
              total={numberOfDeliveries}
            />
          ) : null}
          {showPickup ? (
            <WorkTypeExpandedCard variant="pickup" completed={pickupsCompleted} total={numberOfPickups} />
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className={rootClass}>
      <div className="pa-work-block__tag-columns">
        {showDelivery ? (
          showHoverExpanded ? (
            <WorkTypeTagStackWithHover
              variant="delivery"
              completed={deliveriesCompleted}
              total={numberOfDeliveries}
            />
          ) : (
            <WorkTypeTagStack
              variant="delivery"
              completed={deliveriesCompleted}
              total={numberOfDeliveries}
            />
          )
        ) : null}
        {showPickup ? (
          showHoverExpanded ? (
            <WorkTypeTagStackWithHover
              variant="pickup"
              completed={pickupsCompleted}
              total={numberOfPickups}
            />
          ) : (
            <WorkTypeTagStack variant="pickup" completed={pickupsCompleted} total={numberOfPickups} />
          )
        ) : null}
      </div>
    </div>
  );
};
